import { Backdrop, Card, CircularProgress, Link, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { Box } from "@mui/system";
import { Fragment, useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import PerfBar from "../components/Chart/PerfBar";
import PieCard from "../components/Chart/PieCard";
import ResourceTable from "../components/Chart/ResourceTable";

// import test from "../test.json";
const metricSettings = [
    {
        entry: "speedIndex",
        title: "Speed Index",
        description: <span>Speed Index shows how quickly the contents of a page are visibly populated. <Link href="https://web.dev/speed-index/">Read More</Link></span>,
        p1: 3.4,
        p2: 5.8,
    },
    {
        entry: "first-contentful-paint",
        title: "FCP",
        description: <span>First Contentful Paint marks the time at which the first text or image is painted. <Link href="https://web.dev/first-contentful-paint/">Read More</Link></span>,
        p1: 1.8,
        p2: 3.0,
    },
    {
        entry: "ttfb",
        title: "TTFB",
        description: <span>Time to First Byte measures the time between the request for a resource and when the first byte of a response begins to arrive. <Link href="https://web.dev/ttfb/">Read More</Link></span>,
        p1: 0.2,
        p2: 0.5,
    },
];

const ResultBox = styled(Box)(
    ({ theme }) => ({
        position: "relative",
        display: "flex",
        flexWrap: "wrap",
        padding: theme.spacing(0, "4vw"),
        paddingTop: "6.4rem",
        [theme.breakpoints.up("sm")]: {
            paddingTop: "7rem",
        },
    })
);

const PieColumn = styled(Box)(
    ({ theme }) => ({
        width: "100%",
        display: "flex",
        flexDirection: "column",
        [theme.breakpoints.up("md")]: {
            paddingRight: 0,
            width: "32rem",
        },
        [theme.breakpoints.up("xl")]: {
            width: "40%",
            minWidth: "48rem",
        },
    })
);

const PieColumnItem = styled(Box)(
    ({ theme }) => ({
        margin: theme.spacing(2, 0),
        width: "100%",
        display: "flex",
        justifyContent: "center",
    })
);

const PerfColumn = styled(Box)(
    ({ theme }) => ({
        display: "flex",
        flexDirection: "column",
        width: "100%",
        [theme.breakpoints.up("md")]: {
            width: "calc(100% - 32rem)",
            paddingLeft: "4vw",
            marginTop: theme.spacing(1),
        },
        [theme.breakpoints.up("xl")]: {
            width: "min(60%, calc(100% - 48rem))",
        },
    })
);

const PerfCard = styled((props) => (
    <Card elevation={5} {...props} />
))(({ theme }) => ({
    padding: theme.spacing(1.5, 2, 2.5, 2),
    margin: theme.spacing(2, 0),
}));

const PerfCardTitleBox = styled(Box)(
    ({ theme }) => ({
        display: "flex",
        justifyContent: "space-between",
        flexWrap: "nowrap",
        padding: theme.spacing(0.5),
    })
);

const PerfRowTitle = styled((props) => (
    <Typography variant="h3" {...props} />
))(({ theme }) => ({
    fontSize: "1.9rem",
    fontWeight: 700,
    fontFamily: "'montserrat'",
    position: "relative",
    marginBottom: theme.spacing(0.5),
}));

const MetricCaption = styled((props) => (
    <Typography variant="h5" {...props} />
))(({ theme }) => ({
    fontSize: "1.3rem",
    fontWeight: 500,
    fontFamily: "'montserrat'",
    margin: theme.spacing(1, 1, 4.5, 1),
    color: "#4a4a4a",
}));

export default function Result(props) {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [data, setData] = useState(null);
    const [msg, setMsg] = useState(`Checking from ${searchParams.get("from")}, please wait up to 60 seconds.`);
    const { state } = useLocation();

    useEffect(() => {
        if (!state) {
            window.alert("Please search again.");
            navigate("/", { replace: true });
        }
    }, [state, navigate]);

    const { serverURL } = state || {};

    useEffect(() => {
        // setData(test);
        if (!serverURL) return;
        const fetchAbort = new AbortController();
        setTimeout(() => fetchAbort.abort(), 60000);
        let url = searchParams.get("url");
        if (!url.startsWith("http")) {
            url = `https://${url}`;
        }
        fetch(serverURL, {
            method: "POST",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify({
                url, reserves: {
                    fields: ["time"],
                }, trace: false
            }),
            signal: fetchAbort.signal,
        })
            .then((resp) => resp.json())
            .then((data) => setData(data))
            .catch((exc) => setMsg(`${exc.toString()}. Please try again.`));
    }, [serverURL, searchParams]);

    console.log(data);

    return (
        <ResultBox>
            {data ? (
                <Fragment>
                    <PieColumn>
                        <PieColumnItem>
                            <PieCard
                                title="Who's Delivering"
                                data={data.overview.providers}
                                icon="🛵"
                            />
                        </PieColumnItem>
                        <PieColumnItem>
                            <PieCard
                                title="PoP Locations"
                                data={data.overview.locations}
                                icon="🌎"
                            />
                        </PieColumnItem>
                        <PieColumnItem>
                            <PieCard
                                title="Edge Servers"
                                data={data.overview.pops}
                                icon="🌩"
                            />
                        </PieColumnItem>
                    </PieColumn>
                    <PerfColumn>
                        {metricSettings.map(({ entry, title, description, p1, p2 }) => (
                            Boolean(data.metrics[entry]) && (
                                <PerfCard key={entry}>
                                    <PerfCardTitleBox>
                                        <PerfRowTitle>{title}</PerfRowTitle>
                                        <PerfRowTitle><span style={{ color: "purple" }}>{data.metrics[entry].toFixed(2)}</span> ms</PerfRowTitle>
                                    </PerfCardTitleBox>
                                    <MetricCaption>
                                        {description}
                                    </MetricCaption>
                                    <PerfBar p1={p1} p2={p2} score={data.metrics[entry] / 1000} />
                                </PerfCard>
                            )
                        ))}
                        <ResourceTable data={data.data} />
                    </PerfColumn>
                </Fragment>
            ) : (
                <Backdrop open={true} unmountOnExit sx={{
                    flexDirection: "column",
                    alignItems: "center",
                }}>
                    <CircularProgress color="secondary" />
                    <Typography sx={{
                        margin: 2,
                        fontFamily: "montserrat",
                        fontWeight: 500,
                        display: "block",
                        textAlign: "center",
                    }}>
                        {msg}
                    </Typography>
                </Backdrop>
            )}
        </ResultBox>
    );
};